import { createApp as createVue, h, type App as VueApp, type Plugin } from "vue";
import { setTheme } from "@king-design/vue";
import { scubablue as scubablueTheme } from "@king-design/themes";
import { VueQueryPlugin } from "@tanstack/vue-query";
import App from "./App.vue";
import { registerFormMethods } from "./form-rules";
import { buildMenus } from "./menus";
import { createRouter } from "./router";
import { createStore } from "./store/store";
import { HorizontalGroup } from "kdc-components-vue";
import { apolloClientPlugin } from "./apollo";
import { setupAxiosInstance } from "./axios";
import { queryClient } from "@kce2/common/api/tanstack";
import { devOnlyAsyncComponent } from "@kce2/common/utils/devOnly";
import { vFallbackImage } from "@kce2/common/directive/VFallbackImage";

setTheme(scubablueTheme);

// 注入 :root --k-xxx CSS 变量，供手写样式取色（kebab-case，primary -> --k-primary）。
// 单一事实源为 @king-design/themes 的 scubablue，换肤只升级该包。
const toKebab = (k: string) => k.replace(/[A-Z]/g, m => "-" + m.toLowerCase());
const themeColor = scubablueTheme.color;
if (themeColor && typeof themeColor === "object") {
  Object.entries(themeColor).forEach(([k, v]) => {
    if (typeof v === "string") {
      document.documentElement.style.setProperty(`--k-${toKebab(k)}`, v);
    }
  });
}
// scubablue 无 lightPrimary/primary-200 token，项目扩展（Figma Primary 色阶）：
// Primary-100 选中/悬停浅底（卡片选中、下拉选中项等）、Primary-200 更浅的辅助底（轮播指示条等）
document.documentElement.style.setProperty("--k-light-primary", "#EDF2FF");
document.documentElement.style.setProperty("--k-primary-200", "#C2D0FF");

registerFormMethods();

export const appPlugins: (Plugin | [Plugin, any])[] = [];
let _app: VueApp;

export function getApp() {
  return _app;
}

export async function createApp(root: string | Element) {
  await buildMenus();
  const router = createRouter();
  const store = createStore();
  setupAxiosInstance(router);

  const app = createVue({
    render: () => h(App),
  });

  app.directive("fallback-image", vFallbackImage);

  // 开发环境将 __DEV__ 暴露到全局，供模板访问
  if (__DEV__) {
    app.config.globalProperties.__DEV__ = __DEV__;
  }
  _app = app;
  appPlugins.push([VueQueryPlugin, { queryClient }], apolloClientPlugin, router, store);

  app.use(VueQueryPlugin, {
    queryClient,
  });
  app.use(apolloClientPlugin);
  app.use(router);
  app.use(store);
  app.component("HorizontalGroup", HorizontalGroup);
  app.component(
    "DevDebug",
    devOnlyAsyncComponent(() => import("../features/Cluster/components/DebugPanel/DevDebug.vue")),
  );

  app.mount(root);
  return app;
}

declare module "vue" {
  interface ComponentCustomProperties {
    __DEV__: typeof __DEV__;
  }

  interface GlobalComponents {
    DevDebug: typeof import("../features/Cluster/components/DebugPanel/DevDebug.vue");
  }
}
